import React, { useEffect, useState } from 'react';

import { authorizedPartnersService } from 'services';
import { AuthorizedPartner } from 'interfaces';
import { Spinner } from 'components';

import './AuthorizedPartners.scss';

export const AuthorizedPartners = () => {
  const [partners, setPartners] = useState<AuthorizedPartner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<AuthorizedPartner | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const loadPartners = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        const data = await authorizedPartnersService.getAll();
        const sorted = [...data].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          })
        );
        setPartners(sorted);
      } catch (e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadPartners();
  }, []);

  useEffect(() => {
    if (!selectedPartner) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedPartner(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPartner]);

  const getInitials = (name: string | null) => {
    if (!name) {
      return '?';
    }

    const words = name.split(' ').filter(Boolean);
    if (!words.length) {
      return '?';
    }

    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  };

  const closeModal = () => setSelectedPartner(null);

  return (
    <div className="authorized-partners">
      <h1 className="authorized-partners__title">Authorized Partners</h1>
      <p className="authorized-partners__subtitle">
        Choose your separator maker
      </p>

      {isLoading && (
        <div className="authorized-partners__spinner">
          <Spinner />
        </div>
      )}

      {isError && !isLoading && (
        <div className="authorized-partners__error">
          There was a problem loading authorized partners. Please try again
          later.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="authorized-partners__grid">
          {partners.map((partner) => (
            <button
              key={partner.id}
              type="button"
              className={`authorized-partners__tile${
                selectedPartner?.id === partner.id
                  ? ' authorized-partners__tile--active'
                  : ''
              }`}
              onClick={() => setSelectedPartner(partner)}
            >
              <div className="authorized-partners__logo">
                {partner.logoUrl ? (
                  <img src={partner.logoUrl} alt={partner.name || ''} />
                ) : (
                  <span>{getInitials(partner.name)}</span>
                )}
              </div>
              <div className="authorized-partners__name">
                {partner.name || 'Unknown'}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedPartner && (
        <div
          className="authorized-partners__backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="authorized-partners__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={selectedPartner.name || 'Partner details'}
          >
            <button
              type="button"
              className="authorized-partners__close"
              onClick={closeModal}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="authorized-partners__modal-title">
              {selectedPartner.name || 'Unknown'}
            </h2>
            <div className="authorized-partners__details-row">
              <span className="authorized-partners__details-label">Website</span>
              {selectedPartner.homepage ? (
                <a
                  href={
                    selectedPartner.homepage.startsWith('http')
                      ? selectedPartner.homepage
                      : `https://${selectedPartner.homepage}`
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {selectedPartner.homepage}
                </a>
              ) : (
                <span className="authorized-partners__details-value">
                  Not available
                </span>
              )}
            </div>
            <div className="authorized-partners__details-row">
              <span className="authorized-partners__details-label">E-mail</span>
              {selectedPartner.email ? (
                <a href={`mailto:${selectedPartner.email}`}>
                  {selectedPartner.email}
                </a>
              ) : (
                <span className="authorized-partners__details-value">
                  Not available
                </span>
              )}
            </div>
            <div className="authorized-partners__details-row">
              <span className="authorized-partners__details-label">Telephone</span>
              {selectedPartner.telephone ? (
                <a href={`tel:${selectedPartner.telephone}`}>
                  {selectedPartner.telephone}
                </a>
              ) : (
                <span className="authorized-partners__details-value">
                  Not available
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
